import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/pro-light-svg-icons";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import Box from "@ternary/web-ui-lib/components/Box";
import { Theme } from "@ternary/web-ui-lib/theme/default";
import prettyBytes from "pretty-bytes";
import React, { useRef, useState } from "react";
import copyText from "../copyText";
import IconFileUpload from "../icons/IconFileUpload";

interface FileUploaderProps {
  acceptedFileTypes?: React.HTMLInputTypeAttribute[];
  disabled?: boolean;
  dropMessageStyle?: React.CSSProperties;
  errorCaption?: string;
  hoverTitle?: string;
  maxSize?: number;
  name?: string;
  onChange: (file: File | null) => void;
}

const fileUploadTheme = (baseTheme: Theme) => ({
  ...baseTheme,
  FileUpload_backgroundColor: baseTheme.input_background_color,
  FileUpload_hoverBackgroundColor: baseTheme.primary_color_background_inverse,
});

const DropContainer = styled.div<{
  disabled: boolean;
  error?: boolean;
  isDragging: boolean;
  theme?: Theme;
}>((props) => {
  const theme = fileUploadTheme(props.theme);

  return {
    alignItems: "center",
    backgroundColor: props.disabled
      ? theme.background_color_disabled
      : props.isDragging
        ? theme.FileUpload_hoverBackgroundColor
        : theme.FileUpload_backgroundColor,
    border: `1px solid ${
      props.isDragging
        ? theme.primary_color_text
        : props.error
          ? theme.feedback_negative
          : theme.secondary_color_border
    }`,
    borderRadius: theme.borderRadius_2,
    cursor: props.disabled ? "not-allowed" : "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    opacity: props.disabled ? 0.6 : 1,
    padding: `${theme.space_md} ${theme.space_lg}`,
    transition: "all 0.2s ease-in-out",
  };
});

const HiddenInput = styled.input`
  display: none;
`;

const IconContainer = styled.div((props) => ({
  alignItems: "center",
  backgroundColor: props.theme.elevated_background_color,
  border: `1px solid ${props.theme.secondary_color_border}`,
  borderRadius: props.theme.borderRadius_2,
  boxShadow: props.theme.box_shadow2,
  display: "flex",
  justifyContent: "center",
  padding: props.theme.space_xs,
}));

export function FileUploader({
  acceptedFileTypes = [],
  disabled = false,
  dropMessageStyle = {},
  errorCaption,
  hoverTitle = copyText.fileUploaderHoverTitle,
  maxSize,
  name = "file",
  onChange,
}: FileUploaderProps): JSX.Element {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
  } | null>(null);

  let acceptedFileTypesLabel = acceptedFileTypes.join(" or ");

  if (acceptedFileTypes.length > 1) {
    acceptedFileTypesLabel = acceptedFileTypes
      .slice(0, -1)
      .join(", ")
      .concat(` or ${acceptedFileTypes[acceptedFileTypes.length - 1]}`);
  }

  const handleChange = (file: File | null) => {
    if (file) {
      setFileInfo({
        name: file.name,
        size: file.size,
      });
    } else {
      setFileInfo(null);
    }

    onChange(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    handleChange(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    handleChange(file);
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <Box width="100%">
      <DropContainer
        disabled={disabled}
        error={!!errorCaption}
        isDragging={isDragging}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <HiddenInput
          accept={acceptedFileTypes
            .map((type) => `.${type.toLowerCase()}`)
            .join(",")}
          disabled={disabled}
          name={name}
          ref={inputRef}
          type="file"
          onChange={handleInputChange}
        />
        {fileInfo ? (
          <Flex
            alignItems="center"
            gap={theme.space_sm}
            justifyContent="center"
            width={"100%"}
          >
            <Icon color={theme.text_color_secondary} icon={faFile} size="2x" />
            <Flex
              alignItems="flex-start"
              direction="column"
              justifyContent="center"
              width={"100%"}
            >
              <Flex
                alignItems="center"
                justifyContent="space-between"
                width={"100%"}
              >
                <Text
                  fontSize={theme.fontSize_small}
                  fontWeight={theme.fontWeight_semiBold}
                >
                  {fileInfo.name}
                </Text>
                <Button
                  type="button"
                  iconStart={<Icon icon={faTrashAlt} />}
                  size="tiny"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setFileInfo(null);
                    onChange(null);
                  }}
                />
              </Flex>
              <Text
                color={theme.text_color_secondary}
                fontSize={theme.fontSize_small}
              >
                {errorCaption
                  ? copyText.fileUploaderUploadError
                  : prettyBytes(fileInfo.size)}
              </Text>
              <Text
                color={theme.text_color_caption}
                fontSize={theme.fontSize_small}
                fontWeight={theme.fontWeight_bold}
              >
                {errorCaption}
              </Text>
            </Flex>
          </Flex>
        ) : (
          <Flex alignItems="center" direction="column" justifyContent="center">
            <IconContainer>
              <IconFileUpload width={"20px"} stroke={theme.text_color} />
            </IconContainer>
            {isDragging ? (
              <Text
                color={
                  disabled
                    ? theme.text_color_disabled
                    : theme.primary_color_text
                }
                fontSize={theme.fontSize_small}
                fontWeight={theme.fontWeight_semiBold}
                marginTop={theme.space_xs}
                style={dropMessageStyle}
              >
                {hoverTitle}
              </Text>
            ) : (
              <Flex marginTop={theme.space_xs}>
                <Text
                  color={
                    disabled
                      ? theme.text_color_disabled
                      : theme.primary_color_text
                  }
                  fontSize={theme.fontSize_small}
                  fontWeight={theme.fontWeight_semiBold}
                  style={dropMessageStyle}
                >
                  {copyText.fileUploaderLabelPrimary}
                </Text>
                <Text
                  color={theme.text_color_secondary}
                  fontSize={theme.fontSize_small}
                  style={dropMessageStyle}
                >
                  &nbsp;{copyText.fileUploaderLabelSecondary}
                </Text>
              </Flex>
            )}

            {acceptedFileTypes.length > 0 && (
              <Text
                color={theme.text_color_secondary}
                fontSize={theme.fontSize_xs}
              >
                {acceptedFileTypesLabel}{" "}
                {maxSize
                  ? copyText.fileUploaderMaxSize.replace(
                      "%MAX_SIZE%",
                      prettyBytes(maxSize)
                    )
                  : ""}
              </Text>
            )}
          </Flex>
        )}
      </DropContainer>
    </Box>
  );
}

export const validateFileUpload = (
  file: File | null,
  {
    errorMessages,
    maxSize,
    minSize,
    acceptedFileTypes,
  }: {
    maxSize?: number;
    acceptedFileTypes?: string[];
    minSize?: number;
    errorMessages?: {
      maxSizeValidation?: string;
      minSizeValidation?: string;
      typeValidation?: string;
    };
  }
): string | undefined => {
  if (!file) {
    return copyText.fileUploaderMissingFileValidationError;
  }

  // Validate file type
  if (acceptedFileTypes && acceptedFileTypes.length > 0) {
    const fileExtension = file?.name.split(".").pop()?.toUpperCase() || "";
    if (
      !acceptedFileTypes
        .map((type) => type.toUpperCase())
        .includes(fileExtension)
    ) {
      return (
        errorMessages?.typeValidation ||
        copyText.fileUploaderTypeValidationError.replace(
          "%TYPE%",
          acceptedFileTypes.join(", ")
        )
      );
    }
  }

  // Validate file size
  if (maxSize && file.size > maxSize) {
    return (
      errorMessages?.maxSizeValidation ||
      copyText.fileUploaderMaxSizeValidationError.replace(
        "%SIZE%",
        prettyBytes(maxSize)
      )
    );
  }

  if (minSize && file.size < minSize) {
    return (
      errorMessages?.minSizeValidation ||
      copyText.fileUploaderMinSizeValidationError.replace(
        "%SIZE%",
        prettyBytes(minSize)
      )
    );
  }

  return undefined;
};
